import { Component, computed, input, model, output, signal } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { ProductKeys } from '@config/keys/product.keys';
import { ICartItem } from '@core/models/cart.model';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { DoodProductModel, IOverridesPrices } from '@core/models/product.model';
import { IAdditionGroup } from '@core/models/shop.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { KioskSelectDeclinableProductsComponent } from '@shared/atoms/kiosk/kiosk-select-declinable-products/kiosk-select-declinable-products.component';
import { KioskSimpleProductDetailsOrganism } from '@shared/atoms/kiosk/kiosk-simple-product-details-organism/kiosk-simple-product-details-organism.component';
import { DeclinableProductCart } from '@shared/local-store/cart/cart-draft-declinable-product.store';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-kiosk-product-details-organism',
  standalone: true,
  imports: [
    ContentBuilderColorPipe,
    KioskSimpleProductDetailsOrganism,
    KioskSelectDeclinableProductsComponent,
  ],
  template: `
    <div
      class="flex flex-col gap-5"
      [style.backgroundColor]="backgroundColor() | contentBuilderColor"
      [style.backgroundImage]="backgroundImageCss()"
      [style.backgroundSize]="backgroundImageCss() ? 'cover' : null"
      [style.backgroundPosition]="backgroundImageCss() ? 'center' : null"
      [style.backgroundRepeat]="backgroundImageCss() ? 'no-repeat' : null"
      [style.color]="textColor() | contentBuilderColor"
    >
      @if ((product().allergens?.length ?? 0) > 0) {
        <div>
          <div
            class="gap-0 border-[1px] border-solid"
            [style.borderColor]="textColor() | contentBuilderColor"
          ></div>
          <div
            class="border-[1px] border-solid"
            [style.borderColor]="textColor() | contentBuilderColor"
          ></div>
        </div>
      }
      <div class="mt-5">
        @if (product().type === 'SIMPLE') {
          <div class="mb-10">
            <app-kiosk-simple-product-details-organism
              [textColor]="textColor()"
              [cardColor]="cardColor()"
              [secondaryColor]="secondaryColor()"
              [backgroundColor]="backgroundColor()"
              [primaryButtonBackgroundColor]="primaryButtonBackgroundColor()"
              [unavailableButtonBackground]="unavailableButtonBackground()"
              [unavailableButtonText]="unavailableButtonText()"
              [primaryButtonTextColor]="primaryButtonTextColor()"
              [secondaryColor]="secondaryColor()"
              [simpleProduct]="product()"
              [activeShopId]="activeShopId()"
              [initialCart]="initialCart()"
              (cartData)="cartChange$.next($event)"
            ></app-kiosk-simple-product-details-organism>
          </div>
        }

        @if (product().type === 'DECLINABLE') {
          <div class="mb-10">
            <app-kiosk-select-declinable-products-organism
              [textColor]="textColor()"
              [cardColor]="cardColor()"
              [buttonBackgroundColor]="buttonBackgroundColor()"
              [buttonTextColor]="buttonTextColor()"
              [secondaryColor]="secondaryColor()"
              [backgroundColor]="backgroundColor()"
              [primaryButtonBackgroundColor]="primaryButtonBackgroundColor()"
              [unavailableButtonBackground]="unavailableButtonBackground()"
              [unavailableButtonText]="unavailableButtonText()"
              [primaryButtonTextColor]="primaryButtonTextColor()"
              [secondaryColor]="secondaryColor()"
              [activeShopId]="activeShopId()"
              [declinableProduct]="product()"
              [initialCart]="initialCart()"
              (cartData)="cartChange$.next($event)"
              (additionGroupName)="onAdditionsByGroupChange($event)"
            ></app-kiosk-select-declinable-products-organism>
          </div>
        }
      </div>
    </div>
  `,
})
export class KioskProductDetailsOrganismComponent {
  protected readonly cartChange$ = new Subject<
    | {
        cart: DeclinableProductCart;
        isValid: boolean;
      }
    | undefined
  >();

  public readonly cardColor = input<IContentBuilderFieldColor>();
  public readonly primaryButtonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly primaryButtonTextColor = input<IContentBuilderFieldColor>();
  public readonly textColor = input<IContentBuilderFieldColor>();
  public readonly backgroundColor = input<IContentBuilderFieldColor>();
  public readonly backgroundImage = input<IContentBuilderFieldImage>();
  protected readonly backgroundImageCss = computed(() => {
    const url = this.backgroundImage()?.url;
    return url ? `url(${url})` : null;
  });
  public readonly secondaryColor = input<IContentBuilderFieldColor>();
  public readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly buttonTextColor = input<IContentBuilderFieldColor>();
  public readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  public readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  public readonly displayPriceInfo = input<boolean>(true);
  public readonly initialCart = input<ICartItem | undefined>();

  public readonly product = model.required<Readonly<DoodProductModel>>();
  public readonly activeShopId = input.required<string>();
  public readonly simpleProductAdditionsByGroups = input.required<IAdditionGroup[]>(); // required for simple product
  public readonly simpleProductPriceOverrides = input<IOverridesPrices | undefined>();

  public readonly additionGroupName = output<string>();

  public readonly cartData = outputFromObservable(this.cartChange$);

  //TODO HANDLE LOADING

  protected readonly allergensToggled = signal(false);

  protected get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  protected countChange(newQuantity: number) {
    this.product.update(state => {
      return {
        ...state,
        quantity: newQuantity,
      };
    });
  }

  protected onAdditionsByGroupChange(additionName: any) {
    this.additionGroupName.emit(additionName);
  }
}
